import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { platformClient } from '../modules/platform/api/platform.client';
import { getPublicBranding } from '../modules/platform/api/branding';
import { platformBrandingFallback, type EffectiveBranding } from '../modules/platform/api/branding.types';
import { isMalaySchoolLanguage } from '../modules/tutor/tutorTenantLabels';

export interface PublicTenantResult {
  tenantId: string | null;
  isMalay: boolean;
  branding: EffectiveBranding;
  loading: boolean;
  error: string | null;
}

export interface PublicTenantResolutionOptions {
  pathname: string;
  langParam?: string | null;
  tenantParam?: string | null;
  hostname?: string;
  fetchPublicBrandingFn?: (tenantId: string) => Promise<EffectiveBranding>;
  resolveDomainFn?: (host: string) => Promise<string | null>;
}

export async function resolvePublicTenantContext(
  options: PublicTenantResolutionOptions
): Promise<{ tenantId: string | null; isMalay: boolean; branding: EffectiveBranding; error: string | null }> {
  const pathname = (options.pathname || '').toLowerCase().replace(/\/+$/, '');
  const isExplicitMalayRoute = pathname === '/ms' || pathname === '/malay';

  const langParam = (options.langParam || '').toLowerCase().trim();
  const tenantParam = (options.tenantParam || '').trim();
  const fetchBranding = options.fetchPublicBrandingFn || getPublicBranding;

  const defaultBranding = platformBrandingFallback();

  // Priority 1: Explicit public route (/ms or /malay)
  if (isExplicitMalayRoute) {
    let resolvedBranding = defaultBranding;
    if (tenantParam) {
      try {
        resolvedBranding = await fetchBranding(tenantParam);
      } catch {
        // Fall back to default branding
      }
    }
    return {
      tenantId: tenantParam || null,
      isMalay: true,
      branding: resolvedBranding,
      error: null,
    };
  }

  let domainTenantId: string | null = null;

  // Priority 2: Host / domain resolution (/platform/resolve-domain?host=...)
  if (options.hostname) {
    const host = options.hostname;
    if (host && host !== 'localhost' && host !== '127.0.0.1') {
      try {
        if (options.resolveDomainFn) {
          domainTenantId = await options.resolveDomainFn(host);
        } else {
          const domainRes = await platformClient.get<{ tenant_id?: string }>('/platform/resolve-domain', {
            params: { host },
          });
          domainTenantId = domainRes.data?.tenant_id || null;
        }
      } catch {
        // Domain resolution failed — fallback safely
      }
    }
  }

  // Priority 3: Public tenant branding & language check (from domain resolution)
  if (domainTenantId) {
    try {
      const bData = await fetchBranding(domainTenantId);
      const isMalayLang = Boolean(bData.language && isMalaySchoolLanguage(bData.language));
      return {
        tenantId: domainTenantId,
        isMalay: isMalayLang,
        branding: bData,
        error: null,
      };
    } catch (err: unknown) {
      return {
        tenantId: domainTenantId,
        isMalay: false,
        branding: defaultBranding,
        error: err instanceof Error ? err.message : 'Failed to fetch tenant branding',
      };
    }
  }

  // Priority 4: Query parameters (development/testing overrides)
  if (tenantParam) {
    try {
      const bData = await fetchBranding(tenantParam);
      const isMalayLang = Boolean(bData.language && isMalaySchoolLanguage(bData.language));
      return {
        tenantId: tenantParam,
        isMalay: isMalayLang,
        branding: bData,
        error: null,
      };
    } catch (err: unknown) {
      return {
        tenantId: tenantParam,
        isMalay: false,
        branding: defaultBranding,
        error: err instanceof Error ? err.message : 'Failed to fetch tenant branding',
      };
    }
  }

  if (langParam === 'ms') {
    return {
      tenantId: null,
      isMalay: true,
      branding: defaultBranding,
      error: null,
    };
  }

  // Priority 5: Fallback — non-Malay default LandingPage
  return {
    tenantId: null,
    isMalay: false,
    branding: defaultBranding,
    error: null,
  };
}

export function usePublicTenant(): PublicTenantResult {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [tenantId, setTenantId] = useState<string | null>(null);
  const [isMalay, setIsMalay] = useState<boolean>(false);
  const [branding, setBranding] = useState<EffectiveBranding>(() => platformBrandingFallback());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);

      const langParam = searchParams.get('lang');
      const tenantParam = searchParams.get('tenant') || searchParams.get('tenant_id');
      const hostname = window.location.hostname;

      const res = await resolvePublicTenantContext({
        pathname: location.pathname,
        langParam,
        tenantParam,
        hostname,
      });

      if (!cancelled) {
        setTenantId(res.tenantId);
        setIsMalay(res.isMalay);
        setBranding(res.branding);
        setError(res.error);
        setLoading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, searchParams]);

  return {
    tenantId,
    isMalay,
    branding,
    loading,
    error,
  };
}
